import { useEffect, useState } from "react";
import { NavLink, Outlet, useParams, Link, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Feather, BookOpen, Map, GitBranch, ChevronLeft, Users, Eye, MessageSquareQuote, Languages, Settings, PowerOff, Menu, Quote, History, HeartHandshake, PenLine, Music2, ChefHat } from "lucide-react";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { EntityPanel } from "@/components/EntityPanel";
import { DiscoverDialog } from "@/components/DiscoverDialog";
import { useLive, PresenceBar } from "@/components/LivePresence";
import { useIsLocal, useOffline } from "@/lib/offline/useOffline";
import { CloudOff } from "lucide-react";
import { ExportMenu } from "@/components/ExportMenu";
import { QuickNote } from "@/components/QuickNote";
import { setCustomCategories } from "@/lib/lore";
import { setTemplates } from "@/lib/templates";
import { ProGate } from "@/components/billing/ProGate";
import { Lock } from "lucide-react";

const NAV = [
  { to: "notes", label: "Notes", sub: "AI scribe", icon: Feather },
  { to: "studio", label: "Writing Studio", sub: "Stories", icon: PenLine, studio: true, private: true },
  { to: "lore", label: "Lore Bible", sub: "Wiki", icon: BookOpen },
  { to: "maps", label: "Maps", sub: "Atlas", icon: Map, module: "maps", pro: "maps" },
  { to: "timelines", label: "Timelines", sub: "Chronicle & calendars", icon: GitBranch, module: "timelines", pro: "timelines" },
  { to: "languages", label: "Languages", sub: "Lexicon", icon: Languages, module: "languages", pro: "languages" },
  { to: "relationships", label: "Relationships", sub: "Web & family trees", icon: HeartHandshake, pro: "relationships" },
  { to: "quotes", label: "Quotes", sub: "Library", icon: Quote },
  { to: "recipes", label: "Recipes", sub: "Kitchen", icon: ChefHat, private: true },
  { to: "activity", label: "Edits log", sub: "Activity", icon: History, private: true },
  { to: "ask", label: "Ask the Archive", sub: "Archivist", icon: MessageSquareQuote, private: true },
  { to: "team", label: "Team & sharing", sub: "Members", icon: Users, private: true },
];

export default function UniverseLayout({ isPublic = false }) {
  const { uid } = useParams();
  const { user } = useAuth();
  const [panelEntity, setPanelEntity] = useState(null);
  const { data: universe, isError } = useQuery({ queryKey: ["universe", uid], queryFn: () => api.get(`/universes/${uid}`).then((r) => r.data), retry: false });
  const canEdit = !!universe && !isPublic && ["owner", "editor"].includes(universe.role);
  const base = `/${isPublic ? "p" : "u"}/${uid}`;
  const local = useIsLocal(uid);
  const { offline } = useOffline();
  const liveUsers = useLive(uid, user, !isPublic && !!universe && !!user && !local);
  const { pathname } = useLocation();
  const mods = universe?.modules || {};
  const offModule = NAV.find((n) => n.module && mods[n.module] === false && pathname.startsWith(`${base}/${n.to}`));
  const hasFeat = (f) => !universe || (universe.features || []).includes(f);
  const proLocked = universe && NAV.find((n) => n.pro && !hasFeat(n.pro) && pathname.startsWith(`${base}/${n.to}`));
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => setMenuOpen(false), [pathname]);
  if (universe) { setCustomCategories(universe.custom_categories); setTemplates(universe); }

  const sidebar = (
    <>
        {isPublic ? (
          <div className="flex items-center gap-1.5 px-5 pt-6 text-xs text-[#F59E0B]" data-testid="public-badge"><Eye className="h-3.5 w-3.5" /> Public showcase · read-only</div>
        ) : (
          <Link to="/" className="flex items-center gap-1 px-5 pt-6 text-xs text-[#A8A090] hover:text-[#F3EBDD]" data-testid="back-to-universes-link">
            <ChevronLeft className="h-3.5 w-3.5" /> <img src="/logo.png" alt="LoreRight" className="h-5 w-5 rounded-full" data-testid="sidebar-logo" /> All universes
          </Link>
        )}
        <div className="px-5 pt-6 pb-8 border-b border-[#2D3442]">
          <div className="lf-label mb-2">Universe · {isPublic ? "showcase" : universe?.role}</div>
          <div className="font-display text-2xl leading-tight text-[#F3EBDD]" data-testid="universe-title">{universe?.name || "…"}</div>
          {!isPublic && universe && (
            <NavLink to={`${base}/settings`} data-testid="nav-settings"
              className={({ isActive }) => `mt-2 inline-flex items-center gap-1.5 text-xs transition-colors ${isActive ? "text-[#F59E0B]" : "text-[#A8A090] hover:text-[#F3EBDD]"}`}>
              <Settings className="h-3.5 w-3.5" />Universe settings
            </NavLink>
          )}
        </div>
        {!isPublic && liveUsers.length > 0 && <PresenceBar users={liveUsers} me={user} />}
        <nav className="flex-1 p-3 space-y-1">
          {NAV.filter((n) => (!isPublic || (!n.private && n.to !== "notes")) && (!n.module || mods[n.module] !== false)).map(({ to, label, sub, icon: Icon, pro }) => (
            <NavLink key={to} to={`${base}/${to}`} data-testid={`nav-${to}`}
              className={({ isActive }) => `group flex items-center gap-3 rounded-md px-3 py-2.5 transition-colors ${isActive ? "bg-[#1A202C] text-[#F3EBDD] shadow-[inset_2px_0_0_#D97706]" : "text-[#A8A090] hover:bg-[#13171F] hover:text-[#F3EBDD]"}`}>
              <Icon className="h-4 w-4" />
              <div className="flex-1">
                <div className="text-sm font-medium">{label}</div>
                <div className="text-[10px] uppercase tracking-[0.16em] opacity-60">{sub}</div>
              </div>
              {pro && !hasFeat(pro) && <Lock className="h-3.5 w-3.5 text-[#FBBF24]" data-testid={`nav-lock-${to}`} />}
            </NavLink>
          ))}
          {!isPublic && universe && <ExportMenu uid={uid} canEdit={canEdit} />}
          {!isPublic && (
            <Link to="/inspiration" className="group hidden items-center gap-3 rounded-md px-3 py-2.5 text-[#A8A090] transition-colors hover:bg-[#13171F] hover:text-[#F3EBDD] md:flex" data-testid="nav-inspiration">
              <Music2 className="h-4 w-4" />
              <div className="flex-1"><div className="text-sm font-medium">Inspiration</div><div className="text-[10px] uppercase tracking-[0.16em] opacity-60">Music player</div></div>
            </Link>
          )}
        </nav>
        <div className="p-5 border-t border-[#2D3442] text-xs text-[#A8A090]">
          {isPublic ? <Link to="/login" className="hover:text-[#F59E0B]" data-testid="public-signin-link">Made with LoreRight · Sign in</Link> : user?.email}
        </div>
    </>
  );

  return (
    <div className="flex h-[100dvh] flex-col overflow-hidden bg-[#0B0D10] md:flex-row">
      <aside className="hidden w-64 flex-shrink-0 bg-[#0F131A] border-r border-[#2D3442] md:flex flex-col overflow-y-auto lf-scroll" data-testid="universe-sidebar">{sidebar}</aside>
      <header className="flex items-center gap-3 border-b border-[#2D3442] bg-[#0F131A] px-3 py-2 md:hidden" data-testid="mobile-topbar">
        <button onClick={() => setMenuOpen(true)} className="flex h-10 w-10 items-center justify-center rounded-md text-[#F3EBDD] hover:bg-[#1A202C]" aria-label="Open menu" data-testid="mobile-menu-button"><Menu className="h-5 w-5" /></button>
        <div className="min-w-0 flex-1 truncate font-display text-xl text-[#F3EBDD]" data-testid="mobile-universe-title">{universe?.name || "…"}</div>
        {!isPublic && liveUsers.length > 1 && <span className="text-xs text-[#10B981]" data-testid="mobile-live-count">{liveUsers.length} online</span>}
      </header>
      <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
        <SheetContent side="left" className="flex w-72 flex-col overflow-y-auto border-[#2D3442] bg-[#0F131A] p-0" data-testid="mobile-sidebar">
          <SheetTitle className="sr-only">Menu</SheetTitle>
          <SheetDescription className="sr-only">Universe navigation</SheetDescription>
          {sidebar}
        </SheetContent>
      </Sheet>
      <main className="flex-1 overflow-y-auto lf-scroll lf-glow relative">
        {local && !isPublic && (
          <button onClick={() => window.dispatchEvent(new Event("lf-open-sync"))} className="sticky top-0 z-20 flex w-full items-center justify-center gap-2 border-b border-[#D97706]/30 bg-[#1A1508]/95 px-4 py-1.5 text-xs text-[#FBBF24] backdrop-blur" data-testid="offline-banner">
            <CloudOff className="h-3.5 w-3.5" />Working from this device. Changes stay here until you push them.
          </button>
        )}
        {isError && offline && (
          <div className="h-full flex flex-col items-center justify-center text-center p-10" data-testid="universe-offline-unavailable">
            <CloudOff className="h-8 w-8 text-[#A8A090]" />
            <p className="mt-4 font-display text-4xl">Not saved on this device.</p>
            <p className="mt-2 text-sm text-[#A8A090]">Only universes you own and made available offline open without internet.</p>
            <Link to="/" className="mt-6 text-[#F59E0B] hover:underline" data-testid="offline-home-link">Back to my universes</Link>
          </div>
        )}
        {isError && !offline && (
          <div className="h-full flex flex-col items-center justify-center text-center p-10" data-testid="universe-not-found">
            <p className="font-display text-4xl">This universe is out of reach.</p>
            <p className="mt-2 text-sm text-[#A8A090]">It doesn't exist, or your account doesn't have access to it.</p>
            {!isPublic && <Link to="/" className="mt-6 text-[#F59E0B] hover:underline" data-testid="not-found-home-link">Back to my universes</Link>}
          </div>
        )}
        {universe && offModule && (
          <div className="h-full flex flex-col items-center justify-center text-center p-10" data-testid="module-off">
            <PowerOff className="h-8 w-8 text-[#A8A090]" />
            <p className="mt-4 font-display text-4xl">{offModule.label} is switched off.</p>
            <p className="mt-2 text-sm text-[#A8A090]">Your data is safe. Turn it back on in Universe settings.</p>
            {!isPublic && <Link to={`${base}/settings`} className="mt-6 text-[#F59E0B] hover:underline" data-testid="module-off-settings-link">Open settings</Link>}
          </div>
        )}
        {universe && !offModule && proLocked && <ProGate label={proLocked.label} isOwner={universe.role === "owner"} isPublic={isPublic} />}
        {universe && !offModule && !proLocked && <Outlet context={{ universe, canEdit, base, isPublic, openEntity: setPanelEntity }} />}
      </main>
      <EntityPanel entityId={panelEntity} onClose={() => setPanelEntity(null)} onNavigate={setPanelEntity} base={base} />
      {!isPublic && <DiscoverDialog />}
      {canEdit && !pathname.startsWith(`${base}/notes`) && <QuickNote uid={uid} base={base} />}
    </div>
  );
}
